import type { ApiProblem } from '@rental/contracts/client'
import type { StaffLocale } from '@rental/domain'
import { format, formatEuro, MESSAGES, type StaffMessages } from '@rental/i18n'
import { ProblemError } from './problem-error'

/**
 * A problem the app finds itself, with no answer from the API behind it (A1):
 * the device store could not be read, a file was not of a type the app shows,
 * a file could not be handed to the system's viewer.
 */
export type ClientProblem = {
  kind: 'client'
  code: 'device_store_unavailable' | 'file_type_refused' | 'file_not_opened' | 'unexpected'
}

/** What a screen can show: the API's answer, a dropped connection, or the app's own. */
export type StaffProblem = ApiProblem | ClientProblem

/**
 * A problem in words for staff: their own wording for the staff codes (a
 * wrong PIN, a revoked device), the booking flow's for the codes the public
 * endpoints share (validation, rate limits, an outdated app).
 */
export function problemText(
  problem: StaffProblem,
  locale: StaffLocale,
  staff: StaffMessages,
): string {
  if (problem.kind === 'network') return staff.errors.network
  if (problem.kind === 'client') return staff.errors.client[problem.code]
  // C1: what is missing, in the counter's words: the setting the answer names.
  if (problem.code === 'not_configured' && Array.isArray(problem.details.settings)) {
    const words: Readonly<Record<string, string>> = staff.errors.notConfigured
    const setting = problem.details.settings.find((name) => typeof name === 'string' && words[name])
    return typeof setting === 'string' && words[setting]
      ? words[setting]
      : staff.errors.notConfigured.other
  }
  const own: Readonly<Record<string, string>> = { ...staff.errors.codes, ...staff.errors.shared }
  const shared: Readonly<Record<string, string>> = MESSAGES[locale].errors.codes
  const template = own[problem.code] ?? shared[problem.code] ?? staff.errors.fallback
  // The answer's own details fill the message's placeholders: a wait, a booking code. An
  // amount in cents (`stripeNetCents`) is also there in euros, without its suffix (S9).
  const params: Record<string, string | number> = {}
  for (const [name, value] of Object.entries(problem.details)) {
    if (typeof value === 'string' || typeof value === 'number') params[name] = value
    if (typeof value === 'number' && name.endsWith('Cents')) {
      params[name.slice(0, -'Cents'.length)] = formatEuro(value, locale)
    }
  }
  return format(template, params)
}

/**
 * S2: what a query's or a mutation's error means for the screen. An answer
 * from the API is its problem; anything else (a bug, a malformed answer) is
 * `unexpected`, shown in words instead of leaving a spinner up for good, and
 * reported by the query cache (access-context.tsx). Never null for an error.
 */
export function problemOf(error: unknown): StaffProblem | null {
  if (error === null || error === undefined) return null
  if (error instanceof ProblemError) return error.problem
  return { kind: 'client', code: 'unexpected' }
}
